import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiUser, FiTruck, FiFileText, FiCheck, FiPlus, FiPercent, FiCreditCard, FiFolder, FiX } from 'react-icons/fi';
import { FaCar, FaCalculator, FaHandshake } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import DigitalDealJacketSimple from './DigitalDealJacketSimple';
import EContractManagerFixed from './EContractManagerFixed';

const DealBuilder = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [showDealJacket, setShowDealJacket] = useState(false);
  const [showContractManager, setShowContractManager] = useState(false);
  const [savedDealId, setSavedDealId] = useState(null);
  const [dealData, setDealData] = useState({
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      dateOfBirth: '',
      ssn: '',
      driversLicense: '',
      monthlyIncome: 0,
      creditScore: 0,
      employer: '',
      employmentDuration: ''
    },
    vehicle: {
      id: '',
      year: '',
      make: '',
      model: '',
      vin: '',
      msrp: 0,
      invoicePrice: 0
    },
    pricing: {
      vehiclePrice: 0,
      discount: 0,
      rebates: 0,
      sellingPrice: 0,
      docFee: 299,
      registrationFee: 150,
      salesTaxRate: 8.25,
      salesTaxAmount: 0
    },
    trade: {
      hasTradeIn: false,
      year: '',
      make: '',
      model: '',
      mileage: 0,
      acv: 0,
      payoff: 0,
      netValue: 0
    },
    products: {
      warranty: { selected: false, price: 1995, cost: 1200, name: 'Extended Warranty 5yr/60k' },
      gap: { selected: false, price: 795, cost: 400, name: 'GAP Insurance' },
      maintenance: { selected: false, price: 995, cost: 600, name: 'Maintenance Plan 3yr/36k' }
    },
    financing: {
      method: 'finance',
      downPayment: 0,
      termMonths: 72,
      apr: 5.9,
      monthlyPayment: 0,
      amountFinanced: 0
    },
    totals: {
      totalSalePrice: 0,
      totalOutTheDoor: 0,
      grossProfit: 0,
      financeReserve: 0,
      backendGross: 0,
      totalGross: 0
    }
  });

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [lenders, setLenders] = useState([]);

  useEffect(() => {
    loadVehicles();
    loadCustomers();
    loadLeads();
    loadLenders();
  }, []);

  useEffect(() => {
    calculateDeal();
  }, [dealData.pricing.vehiclePrice, dealData.pricing.discount, dealData.pricing.rebates, 
      dealData.trade.hasTradeIn, dealData.trade.acv, dealData.trade.payoff,
      dealData.financing.downPayment, dealData.financing.apr, dealData.financing.termMonths]);

  const loadVehicles = async () => {
    const { data } = await supabase
      .from('vehicles_db2025')
      .select('*')
      .neq('status', 'sold')
      .order('year', { ascending: false });
    setVehicles(data || []);
  };

  const loadCustomers = async () => {
    const { data } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    setCustomers(data || []);
  };

  const loadLeads = async () => {
    const { data } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    setLeads(data || []);
  };

  const loadLenders = async () => {
    const { data } = await supabase
      .from('lenders')
      .select('*')
      .eq('active', true)
      .order('name');
    setLenders(data || []);
  };

  const handleTradeVinDecode = async () => {
    if (!dealData.trade.vin || dealData.trade.vin.length < 17) {
      alert('Please enter a valid 17-character VIN');
      return;
    }

    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${dealData.trade.vin}?format=json`);
      const data = await response.json();
      
      if (data.Results) {
        const getValue = (variableId) => {
          const result = data.Results.find(r => r.VariableId === variableId);
          return result?.Value || '';
        };

        setDealData({
          ...dealData,
          trade: {
            ...dealData.trade,
            year: getValue(29) || dealData.trade.year,
            make: getValue(26) || dealData.trade.make,
            model: getValue(28) || dealData.trade.model
          }
        });
        
        alert('VIN decoded successfully!');
      }
    } catch (error) {
      console.error('Error decoding VIN:', error);
      alert('Failed to decode VIN. Please enter vehicle details manually.');
    }
  };

  const saveDeal = async () => {
    try {
      // Calculate monthly payment before saving
      const { pricing, trade, products, financing } = dealData;
      const sellingPrice = pricing.vehiclePrice - pricing.discount - pricing.rebates;
      const tradeNetValue = trade.hasTradeIn ? trade.acv - trade.payoff : 0;
      const productTotal = Object.values(products).reduce((sum, p) => p.selected ? sum + p.price : sum, 0);
      const taxableAmount = sellingPrice - (tradeNetValue > 0 ? tradeNetValue : 0);
      const salesTaxAmount = (taxableAmount * pricing.salesTaxRate) / 100;
      const totalSalePrice = sellingPrice + productTotal + pricing.docFee + pricing.registrationFee + salesTaxAmount;
      const amountFinanced = totalSalePrice - financing.downPayment - (tradeNetValue > 0 ? tradeNetValue : 0);
      
      const monthlyRate = financing.apr / 100 / 12;
      const monthlyPayment = monthlyRate > 0
        ? (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, financing.termMonths)) / 
          (Math.pow(1 + monthlyRate, financing.termMonths) - 1)
        : amountFinanced / financing.termMonths;

      const { data, error } = await supabase
        .from('deals')
        .insert([
          {
            customer_name: `${dealData.customer.firstName} ${dealData.customer.lastName}`,
            customer_email: dealData.customer.email,
            customer_phone: dealData.customer.phone,
            vehicle_id: dealData.vehicle.id || null,
            vehicle_price: dealData.pricing.vehiclePrice || 0,
            trade_in_value: dealData.trade.acv || 0,
            trade_in_payoff: dealData.trade.payoff || 0,
            down_payment: dealData.financing.downPayment || 0,
            monthly_payment: monthlyPayment || 0,
            total_price: totalSalePrice || 0,
            gross_profit: dealData.totals?.totalGross || 0,
            status: 'pending',
            created_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) throw error;
      
      if (data && data.id) {
        setSavedDealId(data.id);
        alert('Deal saved successfully! You can now create contracts.');
      } else {
        alert('Deal saved but no ID returned. Please refresh.');
      }
      
    } catch (error) {
      console.error('Error saving deal:', error);
      alert('Failed to save deal: ' + (error.message || 'Please check all required fields.'));
    }
  };

  const calculateDeal = () => {
    const { pricing, trade, products, financing } = dealData;
    
    const sellingPrice = pricing.vehiclePrice - pricing.discount - pricing.rebates;
    const tradeNetValue = trade.hasTradeIn ? trade.acv - trade.payoff : 0;
    const productTotal = Object.values(products).reduce((sum, p) => p.selected ? sum + p.price : sum, 0);
    const taxableAmount = sellingPrice - (tradeNetValue > 0 ? tradeNetValue : 0);
    const salesTaxAmount = (taxableAmount * pricing.salesTaxRate) / 100;
    const totalSalePrice = sellingPrice + productTotal + pricing.docFee + pricing.registrationFee + salesTaxAmount;
    const amountFinanced = totalSalePrice - financing.downPayment - (tradeNetValue > 0 ? tradeNetValue : 0);
    
    const monthlyRate = financing.apr / 100 / 12;
    const monthlyPayment = monthlyRate > 0
      ? (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, financing.termMonths)) / 
        (Math.pow(1 + monthlyRate, financing.termMonths) - 1)
      : amountFinanced / financing.termMonths;
    
    const vehicleGross = sellingPrice - (dealData.vehicle.invoicePrice || pricing.vehiclePrice * 0.92);
    const productGross = Object.values(products).reduce((sum, p) => p.selected ? sum + (p.price - p.cost) : sum, 0);
    const financeReserve = financing.method === 'finance' ? amountFinanced * 0.02 : 0;
    
    setDealData(prev => ({
      ...prev,
      totals: {
        totalSalePrice,
        totalOutTheDoor: totalSalePrice,
        grossProfit: vehicleGross,
        financeReserve,
        backendGross: productGross,
        totalGross: vehicleGross + productGross + financeReserve
      }
    }));
  };

  const steps = [
    { number: 1, title: 'Customer', icon: FiUser },
    { number: 2, title: 'Vehicle', icon: FaCar },
    { number: 3, title: 'Trade-In', icon: FiTruck },
    { number: 4, title: 'F&I', icon: FiFileText },
    { number: 5, title: 'Finance', icon: FiCreditCard },
    { number: 6, title: 'Review', icon: FaHandshake }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Deal Builder</h2>
        <p className="text-gray-600">Structure complete vehicle deals</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 flex items-center justify-between">
        {steps.map((step, i) => (
          <div key={step.number} className="flex items-center">
            <button
              onClick={() => setActiveStep(step.number)}
              className={`flex flex-col items-center ${
                activeStep === step.number ? 'text-primary-600' : 
                activeStep > step.number ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                activeStep === step.number ? 'border-primary-600 bg-primary-50' : 
                activeStep > step.number ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
              }`}>
                <SafeIcon icon={step.icon} className="w-5 h-5" />
              </div>
              <span className="text-xs mt-1">{step.title}</span>
            </button>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-full mx-2 ${
                activeStep > step.number ? 'bg-green-600' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            {activeStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Customer Information</h3>
                
                {/* Customer Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Search Existing Customer or Lead</label>
                  <input
                    type="text"
                    placeholder="Search by name, phone, or email..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg mb-2"
                  />
                  
                  {customerSearch && (
                    <div className="border rounded-lg max-h-60 overflow-y-auto">
                      {/* Search in Leads */}
                      {leads
                        .filter(lead => {
                          const search = customerSearch.toLowerCase();
                          return (
                            (lead.first_name?.toLowerCase().includes(search)) ||
                            (lead.last_name?.toLowerCase().includes(search)) ||
                            (lead.email?.toLowerCase().includes(search)) ||
                            (lead.phone?.includes(search))
                          );
                        })
                        .slice(0, 5)
                        .map(lead => (
                          <div
                            key={`lead-${lead.id}`}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                            onClick={() => {
                              setSelectedCustomer(lead);
                              setDealData({
                                ...dealData,
                                customer: {
                                  firstName: lead.first_name || '',
                                  lastName: lead.last_name || '',
                                  email: lead.email || '',
                                  phone: lead.phone || '',
                                  address: lead.address || '',
                                  city: lead.city || '',
                                  state: lead.state || '',
                                  zipCode: lead.zip_code || '',
                                  monthlyIncome: lead.monthly_income || 0,
                                  creditScore: lead.credit_score_range ? parseInt(lead.credit_score_range.split('-')[0]) : 0
                                }
                              });
                              setCustomerSearch('');
                            }}
                          >
                            <div className="font-medium">
                              {lead.first_name} {lead.last_name}
                              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Lead</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {lead.phone} • {lead.email}
                            </div>
                          </div>
                        ))}
                      
                      {/* Search in existing Customers */}
                      {customers
                        .filter(customer => {
                          const search = customerSearch.toLowerCase();
                          return (
                            (customer.first_name?.toLowerCase().includes(search)) ||
                            (customer.last_name?.toLowerCase().includes(search)) ||
                            (customer.email?.toLowerCase().includes(search)) ||
                            (customer.phone?.includes(search))
                          );
                        })
                        .slice(0, 5)
                        .map(customer => (
                          <div
                            key={`customer-${customer.id}`}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setDealData({
                                ...dealData,
                                customer: {
                                  firstName: customer.first_name || '',
                                  lastName: customer.last_name || '',
                                  email: customer.email || '',
                                  phone: customer.phone || '',
                                  address: customer.address || '',
                                  city: customer.city || '',
                                  state: customer.state || '',
                                  zipCode: customer.zip_code || '',
                                  dateOfBirth: customer.date_of_birth || '',
                                  ssn: customer.ssn || '',
                                  driversLicense: customer.drivers_license || '',
                                  monthlyIncome: customer.monthly_income || 0,
                                  creditScore: customer.credit_score || 0,
                                  employer: customer.employer || '',
                                  employmentDuration: customer.employment_duration || ''
                                }
                              });
                              setCustomerSearch('');
                            }}
                          >
                            <div className="font-medium">
                              {customer.first_name} {customer.last_name}
                              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Customer</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {customer.phone} • {customer.email}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {selectedCustomer && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-2">
                      <div className="text-sm text-blue-800">
                        Selected: <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>
                        <button
                          onClick={() => {
                            setSelectedCustomer(null);
                            setDealData({...dealData, customer: {
                              firstName: '',
                              lastName: '',
                              email: '',
                              phone: '',
                              monthlyIncome: 0,
                              creditScore: 0
                            }});
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-700"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={dealData.customer.firstName}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, firstName: e.target.value}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={dealData.customer.lastName}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, lastName: e.target.value}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={dealData.customer.email}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, email: e.target.value}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={dealData.customer.phone}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, phone: e.target.value}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="number"
                    placeholder="Monthly Income"
                    value={dealData.customer.monthlyIncome}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, monthlyIncome: parseFloat(e.target.value) || 0}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="number"
                    placeholder="Credit Score"
                    value={dealData.customer.creditScore}
                    onChange={(e) => setDealData({...dealData, customer: {...dealData.customer, creditScore: parseInt(e.target.value) || 0}})}
                    className="px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Vehicle Selection</h3>
                <select
                  className="w-full px-4 py-2 border rounded-lg"
                  value={dealData.vehicle.id || ''}
                  onChange={(e) => {
                    const v = vehicles.find(v => v.id.toString() === e.target.value);
                    if (v) {
                      setSelectedVehicle(v);
                      setDealData({
                        ...dealData,
                        vehicle: {
                          id: v.id,
                          year: v.year,
                          make: v.make,
                          model: v.model,
                          vin: v.vin,
                          msrp: v.price,
                          invoicePrice: v.price * 0.92
                        },
                        pricing: { ...dealData.pricing, vehiclePrice: v.price }
                      });
                    }
                  }}
                >
                  <option value="">Select Vehicle from Inventory</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      Stock #{v.stock_number} - {v.year} {v.make} {v.model} - ${v.price?.toLocaleString()} - {v.mileage?.toLocaleString()} miles
                    </option>
                  ))}
                </select>
                
                {selectedVehicle && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><strong>VIN:</strong> {selectedVehicle.vin}</div>
                      <div><strong>Color:</strong> {selectedVehicle.exterior_color}</div>
                      <div><strong>Engine:</strong> {selectedVehicle.engine}</div>
                      <div><strong>Transmission:</strong> {selectedVehicle.transmission}</div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Discount</label>
                    <input
                      type="number"
                      value={dealData.pricing.discount}
                      onChange={(e) => setDealData({...dealData, pricing: {...dealData.pricing, discount: parseFloat(e.target.value) || 0}})}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Rebates</label>
                    <input
                      type="number"
                      value={dealData.pricing.rebates}
                      onChange={(e) => setDealData({...dealData, pricing: {...dealData.pricing, rebates: parseFloat(e.target.value) || 0}})}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Trade-In</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dealData.trade.hasTradeIn}
                    onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, hasTradeIn: e.target.checked}})}
                  />
                  <span>Customer has trade-in</span>
                </label>
                
                {dealData.trade.hasTradeIn && (
                  <div className="space-y-4 mt-4">
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-medium text-gray-900 mb-3">Trade-In Vehicle Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                          <input
                            type="number"
                            placeholder="2020"
                            value={dealData.trade.year || ''}
                            onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, year: e.target.value}})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                          <input
                            type="text"
                            placeholder="Toyota"
                            value={dealData.trade.make || ''}
                            onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, make: e.target.value}})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                          <input
                            type="text"
                            placeholder="Camry"
                            value={dealData.trade.model || ''}
                            onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, model: e.target.value}})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Mileage</label>
                          <input
                            type="number"
                            placeholder="50000"
                            value={dealData.trade.mileage || ''}
                            onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, mileage: parseInt(e.target.value) || 0}})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="1HGCV1F3XJA123456"
                              value={dealData.trade.vin || ''}
                              onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, vin: e.target.value}})}
                              onBlur={() => handleTradeVinDecode()}
                              className="flex-1 px-3 py-2 border rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={handleTradeVinDecode}
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                              Decode
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                          <select
                            value={dealData.trade.condition || 'good'}
                            onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, condition: e.target.value}})}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="poor">Poor</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Trade ACV (Actual Cash Value)</label>
                        <input
                          type="number"
                          placeholder="15000"
                          value={dealData.trade.acv}
                          onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, acv: parseFloat(e.target.value) || 0}})}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payoff Amount</label>
                        <input
                          type="number"
                          placeholder="8000"
                          value={dealData.trade.payoff}
                          onChange={(e) => setDealData({...dealData, trade: {...dealData.trade, payoff: parseFloat(e.target.value) || 0}})}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeStep === 4 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">F&I Products</h3>
                {Object.entries(dealData.products).map(([key, product]) => (
                  <label key={key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={product.selected}
                        onChange={(e) => setDealData({
                          ...dealData,
                          products: {...dealData.products, [key]: {...product, selected: e.target.checked}}
                        })}
                      />
                      <span>{product.name}</span>
                    </div>
                    <span className="font-semibold">${product.price}</span>
                  </label>
                ))}
              </div>
            )}

            {activeStep === 5 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Financing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Down Payment</label>
                    <input
                      type="number"
                      value={dealData.financing.downPayment}
                      onChange={(e) => setDealData({...dealData, financing: {...dealData.financing, downPayment: parseFloat(e.target.value) || 0}})}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">APR %</label>
                    <input
                      type="number"
                      step="0.1"
                      value={dealData.financing.apr}
                      onChange={(e) => setDealData({...dealData, financing: {...dealData.financing, apr: parseFloat(e.target.value) || 0}})}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 6 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Deal Review</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-semibold mb-2">Deal Summary</div>
                  <div>Total: ${(dealData.totals?.totalOutTheDoor || 0).toLocaleString()}</div>
                  <div>Monthly: ${(() => {
                    const { pricing, trade, products, financing } = dealData;
                    const sellingPrice = pricing.vehiclePrice - pricing.discount - pricing.rebates;
                    const tradeNetValue = trade.hasTradeIn ? trade.acv - trade.payoff : 0;
                    const productTotal = Object.values(products).reduce((sum, p) => p.selected ? sum + p.price : sum, 0);
                    const taxableAmount = sellingPrice - (tradeNetValue > 0 ? tradeNetValue : 0);
                    const salesTaxAmount = (taxableAmount * pricing.salesTaxRate) / 100;
                    const totalSalePrice = sellingPrice + productTotal + pricing.docFee + pricing.registrationFee + salesTaxAmount;
                    const amountFinanced = totalSalePrice - financing.downPayment - (tradeNetValue > 0 ? tradeNetValue : 0);
                    const monthlyRate = financing.apr / 100 / 12;
                    const monthlyPayment = monthlyRate > 0
                      ? (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, financing.termMonths)) / 
                        (Math.pow(1 + monthlyRate, financing.termMonths) - 1)
                      : amountFinanced / financing.termMonths;
                    return monthlyPayment ? monthlyPayment.toFixed(2) : '0.00';
                  })()}</div>
                  <div>Gross Profit: ${(dealData.totals?.totalGross || 0).toLocaleString()}</div>
                </div>
                <button 
                  onClick={saveDeal}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold">
                  Save Deal
                </button>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                disabled={activeStep === 1}
                className="px-6 py-2 border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setActiveStep(Math.min(6, activeStep + 1))}
                disabled={activeStep === 6}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg"
              >
                Next
              </button>
            </div>
          </motion.div>
        </div>

        {/* Summary Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6 sticky top-20">
            <h3 className="text-xl font-semibold mb-4">Deal Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Vehicle</span>
                <span>${dealData.pricing.vehiclePrice.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>Discount</span>
                <span>-${dealData.pricing.discount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-2">
                <span>Total</span>
                <span>${dealData.totals.totalOutTheDoor.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-green-600 font-semibold">
                <span>Monthly</span>
                <span>${dealData.financing.monthlyPayment.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Deal Documents and Contract Buttons */}
            {savedDealId && (
              <>
                <button
                  onClick={() => setShowDealJacket(true)}
                  className="w-full mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiFolder} className="w-4 h-4" />
                  Open Deal Jacket
                </button>
                <button
                  onClick={() => setShowContractManager(true)}
                  className="w-full mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiFileText} className="w-4 h-4" />
                  Create Contracts
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* E-Contract Manager Modal */}
      {showContractManager && savedDealId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">E-Contract Manager</h2>
              <button
                onClick={() => setShowContractManager(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <EContractManagerFixed
                dealData={{
                  id: savedDealId,
                  ...dealData
                }}
                onContractComplete={(contracts) => {
                  console.log('Contracts completed:', contracts);
                  setShowContractManager(false);
                  alert('Contracts have been sent to the customer!');
                }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Digital Deal Jacket Modal */}
      {showDealJacket && savedDealId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Digital Deal Jacket</h2>
              <button
                onClick={() => setShowDealJacket(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <DigitalDealJacketSimple
                dealId={savedDealId}
                dealData={{
                  ...dealData,
                  deal_number: `DL${savedDealId.slice(0, 8)}`,
                  customer: {
                    id: dealData.customer.id,
                    first_name: dealData.customer.firstName,
                    last_name: dealData.customer.lastName,
                    email: dealData.customer.email,
                    phone: dealData.customer.phone,
                    address: dealData.customer.address,
                    city: dealData.customer.city,
                    state: dealData.customer.state,
                    zip: dealData.customer.zip,
                    drivers_license: dealData.customer.driversLicense
                  },
                  vehicle: dealData.vehicle,
                  sale_price: dealData.pricing.sellingPrice || 0,
                  trade_in_value: dealData.trade?.netValue || 0,
                  sales_tax: dealData.pricing?.salesTaxAmount || 0,
                  title_registration_fee: dealData.pricing.registrationFee || 0,
                  doc_fee: dealData.pricing.docFee || 0,
                  total_amount: dealData.totals?.totalOutTheDoor || 0,
                  down_payment: dealData.financing.downPayment || 0,
                  amount_financed: dealData.totals?.amountFinanced || 0,
                  sale_date: new Date().toISOString()
                }}
                onUpdate={() => {}}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealBuilder;
